import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { createManualProject } from '../../services/api';
import { DEFAULT_MODEL } from '../../constants/models';
import { getDefaultLLMConfig } from '../../constants/llmConfig';
import logger from '../../utils/logger';

import StepPromptType from './steps/StepPromptType';
import StepPrompts from './steps/StepPrompts';
import StepOutputType from './steps/StepOutputType';
import StepSchema from './steps/StepSchema';
import StepVariables from './steps/StepVariables';
import StepMetadata from './steps/StepMetadata';
import StepConfig from './steps/StepConfig';
import StepReview from './steps/StepReview';

const ALL_STEPS = [
  { id: 'promptType', title: 'Prompt Type', component: StepPromptType, required: true },
  { id: 'prompts', title: 'Write Prompts', component: StepPrompts, required: true },
  { id: 'outputType', title: 'Output Format', component: StepOutputType, required: true },
  { id: 'schema', title: 'JSON Schema', component: StepSchema, conditional: true, required: true },
  { id: 'variables', title: 'Variables', component: StepVariables, required: false },
  { id: 'metadata', title: 'Metadata', component: StepMetadata, required: false },
  { id: 'config', title: 'Configuration', component: StepConfig, required: false },
  { id: 'review', title: 'Review', component: StepReview, required: false },
];

const ManualPromptWizard = ({
  projectName,
  projectId,
  workspaceId,
  onComplete
}) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    prompt_type: 'System User', // Default prompt type
    output_type: 'Plain Text', // Default output type
    system_prompt: '',
    user_prompt: '',
    few_shot_examples: '',
    schema: '',
    variables: [],
    use_case: '',
    description: '',
    output_constraints: '',
    must_include: '',
    must_avoid: '',
    special_instructions: '',
  });

  // Get visible steps based on output type (conditional schema step)
  const getVisibleSteps = useCallback(() => {
    return ALL_STEPS.filter(step => {
      if (step.id === 'schema') {
        return formData.output_type === 'Schema';
      }
      return true;
    });
  }, [formData.output_type]);

  const visibleSteps = getVisibleSteps();
  const currentStepData = visibleSteps[currentStep];
  const CurrentStepComponent = currentStepData.component;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === visibleSteps.length - 1;

  const updateFormData = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleNext = useCallback(() => {
    // Validate current step before proceeding
    const stepId = visibleSteps[currentStep]?.id;
    
    if (stepId === 'prompts') {
      const isSystemPromptRequired = formData.prompt_type === 'System User' || formData.prompt_type === 'System  User  Assistant ';
      if (!formData.user_prompt.trim()) {
        toast.error('User prompt is required');
        return;
      }
      if (isSystemPromptRequired && !formData.system_prompt.trim()) {
        toast.error('System prompt is required for this prompt type');
        return;
      }
    }
    
    if (stepId === 'schema') {
      if (!formData.schema || !formData.schema.trim()) {
        toast.error('JSON schema is required');
        return;
      }
      try {
        JSON.parse(formData.schema);
      } catch (e) {
        toast.error('Invalid JSON schema');
        return;
      }
    }
    
    if (currentStep < visibleSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, visibleSteps, formData.prompt_type, formData.user_prompt, formData.system_prompt, formData.schema]);

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  }, [isFirstStep]);

  // Validation function for current step
  const isCurrentStepValid = useCallback(() => {
    const stepId = visibleSteps[currentStep]?.id;
    
    if (stepId === 'prompts') {
      const isSystemPromptRequired = formData.prompt_type === 'System User' || formData.prompt_type === 'System  User  Assistant ';
      if (!formData.user_prompt.trim()) return false;
      if (isSystemPromptRequired && !formData.system_prompt.trim()) return false;
    }
    
    if (stepId === 'schema') {
      if (!formData.schema || !formData.schema.trim()) return false;
      try {
        JSON.parse(formData.schema);
      } catch (e) {
        return false;
      }
    }
    
    return true;
  }, [currentStep, visibleSteps, formData.prompt_type, formData.user_prompt, formData.system_prompt, formData.schema]);

  const handleCreate = useCallback(async (finalData) => {
    setIsCreating(true);
    
    try {
      // Prepare payload
      const payload = {
        project_name: projectName,
        workspace_id: workspaceId,
        prompt_type: finalData.prompt_type,
        system_prompt: finalData.system_prompt || null,
        user_prompt: finalData.user_prompt,
        few_shot_examples: finalData.few_shot_examples || null,
        variables: finalData.variables || [],
        use_case: finalData.use_case || null,
        description: finalData.description || null,
        output_type: finalData.output_type,
        schema: finalData.schema || null,
        additional_config: {
          output_constraints: finalData.output_constraints || null,
          must_include: finalData.must_include || null,
          must_avoid: finalData.must_avoid || null,
          special_instructions: finalData.special_instructions || null,
        },
        model: DEFAULT_MODEL,
        provider: 'groq',
        llm_config: getDefaultLLMConfig(DEFAULT_MODEL),
      };

      logger.userAction('Creating manual project', { projectName });

      const result = await createManualProject(payload);

      if (result.success) {
        toast.success(`Project "${projectName}" created successfully!`);
        logger.info('Manual project created', { projectId: result.data.project_id });
        
        if (onComplete) {
          onComplete(result.data.project_id);
        } else {
          navigate('/prompts');
        }
      } else {
        throw new Error(result.error || 'Failed to create project');
      }
    } catch (error) {
      logger.error('Failed to create manual project', error);
      toast.error(error.message || 'Failed to create project');
    } finally {
      setIsCreating(false);
    }
  }, [projectName, workspaceId, onComplete, navigate]);

  return (
    <div className="w-full h-screen flex justify-center items-center py-6 px-6 bg-bg-primary overflow-hidden">
      <div className="w-full max-w-[850px] h-[calc(100vh-48px)] max-h-[900px] bg-bg-secondary rounded-xl border border-border-primary shadow-2xl overflow-hidden flex flex-col animate-fadeIn">
        {/* Header */}
        <div className="flex items-center gap-2 py-4 px-6 bg-gradient-to-b from-bg-tertiary to-bg-secondary border-b border-border-primary flex-shrink-0">
          <span className="text-[10px] font-semibold text-text-tertiary uppercase tracking-wider">Manual Project:</span>
          <span className="text-base font-semibold text-text-primary font-mono">{projectName}</span>
        </div>

        {/* Progress Bar */}
        <div className="flex-shrink-0 border-b border-border-primary bg-bg-secondary px-6 py-4">
          <div className="flex items-center justify-between">
            {visibleSteps.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                      index < currentStep
                        ? 'bg-primary-500 text-white'
                        : index === currentStep
                        ? 'bg-primary-500 text-white ring-4 ring-primary-500/20'
                        : 'bg-bg-tertiary text-text-secondary border border-border-primary'
                    }`}
                  >
                    {index < currentStep ? '✓' : index + 1}
                  </div>
                  <div
                    className={`mt-2 text-xs font-medium text-center ${
                      index === currentStep ? 'text-text-primary' : 'text-text-secondary'
                    }`}
                  >
                    {step.title}
                  </div>
                </div>
                {index < visibleSteps.length - 1 && (
                  <div
                    className={`flex-1 h-0.5 mx-2 mt-[-20px] transition-all ${
                      index < currentStep ? 'bg-primary-500' : 'bg-border-primary'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden p-0 m-0">
          <div className="flex-1 py-6 px-6 overflow-y-auto overflow-x-hidden min-h-0 [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-track]:my-2 [&::-webkit-scrollbar-thumb]:bg-border-primary [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-bg-secondary [&::-webkit-scrollbar-thumb:hover]:bg-text-tertiary">
            <CurrentStepComponent
              formData={formData}
              updateFormData={updateFormData}
              workspaceId={workspaceId}
            />
          </div>

          <div className="flex justify-between items-center py-4 px-6 border-t border-border-primary bg-bg-secondary flex-shrink-0">
            <button
              type="button"
              onClick={handleBack}
              className="py-2 px-4 text-sm font-medium bg-bg-tertiary text-text-primary border border-border-primary rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-bg-hover hover:border-border-hover disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isFirstStep}
            >
              Back
            </button>

            {isLastStep ? (
              <button
                type="button"
                onClick={() => handleCreate(formData)}
                disabled={isCreating || !formData.user_prompt}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating...
                  </>
                ) : (
                  'Create Prompt'
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={!isCurrentStepValid()}
                className="py-2 px-4 text-sm font-medium bg-primary-600 text-white border border-primary-600 rounded-md cursor-pointer transition-all duration-fast inline-flex items-center justify-center gap-2 whitespace-nowrap hover:bg-primary-500 hover:border-primary-500 hover:-translate-y-px hover:shadow-md active:bg-primary-700 active:border-primary-700 active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

ManualPromptWizard.propTypes = {
  projectName: PropTypes.string.isRequired,
  projectId: PropTypes.string,
  workspaceId: PropTypes.string,
  onComplete: PropTypes.func,
};

export default ManualPromptWizard;

